import { DropdownMenu, DropdownMenuSize } from "react-migration/components/DropdownMenu";
import { WorkspaceEventName, logEventV2 } from "react-migration/lib/util/logEventV2";
import { memo, useCallback, useMemo } from "react";
import { setSelectedWorkspace, setSelectedWorkspaceAndNavigateToSites } from "react-migration/domains/workspaces/util";

import { Button } from "react-migration/components/Button";
import { Icon } from "react-migration/components/Icon";
import { LineLoading } from "react-migration/domains/dashboard/documents/components/LineLoading";
import ROUTES from "src/js/router/routes";
import { Theme } from "react-migration/lib/theme/Theme";
import { Tooltip } from "react-migration/components/Tooltip";
import { WorkspaceDropdownList } from "./WorkspaceDropdownList";
import { WorkspaceDropdownMobile } from "./WorkspaceDropdownMobile";
import clsx from "clsx";
import { openNewWorkspaceModal } from "src/js/stores/navigation/actions";
import { router } from "src/js/router/react-router";
import { useActiveNavTab } from "react-migration/layouts/nav/ActiveNavTabContext";
import { useDropdownOpenState } from "./useDropdownOpenState";
import { useEventListener } from "usehooks-ts";
import { useIsMobile } from "react-migration/lib/theme/hooks/useIsMobile";
import { useSelectedWorkspaceQuery } from "./useSelectedWorkspaceQuery";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

export const WorkspaceDropdown = () => {
  const { isOpen, setIsOpen } = useDropdownOpenState();
  const { selectedWorkspace, error, loading } = useSelectedWorkspaceQuery({ forceUpdateLastOpenedDate: true });
  const { activeTab } = useActiveNavTab();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const triggerLabel = useMemo(() => {
    if (loading || !selectedWorkspace?.title) return <LineLoading />;
    return selectedWorkspace.title;
  }, [loading, selectedWorkspace?.title]);

  const showWorkspaceTitleTooltip = Boolean(selectedWorkspace?.title && !loading);

  useEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Escape" && isOpen) {
      setIsOpen(false);
    }
  });

  const onCreateNewWorkspace = useCallback(() => {
    setIsOpen(false);
    openNewWorkspaceModal("Workspace dropdown");
  }, [setIsOpen]);

  const onSelectWorkspace = useCallback(
    (workspaceId: string) => {
      if (activeTab === "workspaces") {
        setSelectedWorkspaceAndNavigateToSites(workspaceId, WorkspaceEventName.WORKSPACE_DROPDOWN_WORKSPACE_SELECTED);
      } else {
        setSelectedWorkspace(workspaceId, WorkspaceEventName.WORKSPACE_DROPDOWN_WORKSPACE_SELECTED);
      }
      setIsOpen(false);
    },
    [activeTab, setIsOpen]
  );

  const onViewAllWorkspaces = useCallback(() => {
    setIsOpen(false);
    logEventV2({ name: "Workspace dropdown - view all", properties: {} });
    router.push({ name: ROUTES.WORKSPACES });
  }, [setIsOpen]);

  if (error) {
    return null;
  }

  if (isMobile) {
    return (
      <WorkspaceDropdownMobile
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        selectedWorkspace={selectedWorkspace}
        loading={loading}
        onCreateNewWorkspace={onCreateNewWorkspace}
        onSelectWorkspace={onSelectWorkspace}
      />
    );
  }

  return (
    <DropdownMenu.Provider
      size={DropdownMenuSize.AUTO}
      offset={0}
      isOpen={isOpen}
      onMenuClosed={() => setIsOpen(false)}
    >
      <DropdownMenu.Trigger
        data-testid="workspace-dropdown-trigger"
        align={DropdownMenu.TriggerAlignment["BOTTOM_LEFT"]}
        className="p-2!"
        onClick={() => {
          if (!isOpen) {
            logEventV2({ name: "Workspace dropdown - opened", properties: {} });
          }
          setIsOpen(!isOpen);
        }}
        disabled={loading}
      >
        <div
          className={clsx(
            "relative",
            "h-full",
            "w-48",
            "flex",
            "items-center",
            "overflow-hidden",
            "cursor-pointer",
            "grow",
            "gap-x-2",
            "px-1.5",
            "py-px",
            "rounded-lg",
            "bg-white",
            "hover:bg-neutral-100"
          )}
          role="button"
        >
          <div className="flex min-w-0 grow flex-col px-1">
            <div className="font-medium uppercase text-text-base-primary-disabled text-3xs/3.5">
              {t("workspaces.workspace")}
            </div>
            <Tooltip.Wrapper>
              <Tooltip.Trigger asChild>
                <div className="grow truncate select-none text-text-base-primary text-sm font-medium">
                  {triggerLabel}
                </div>
              </Tooltip.Trigger>
              {showWorkspaceTitleTooltip && (
                <Tooltip.Content align="start" side="bottom" theme={Theme.Light} size="sm">
                  <span className="whitespace-normal wrap-break-word">{selectedWorkspace?.title}</span>
                </Tooltip.Content>
              )}
            </Tooltip.Wrapper>
          </div>
          <div>
            <Icon
              size="md"
              cursor="pointer"
              extraClasses="ml-auto"
              icon={isOpen ? "icon-lt-arrow-up-s-line" : "icon-lt-arrow-down-s-line"}
            />
          </div>
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="rounded-t-none! rounded-b-lg z-99999">
        {isOpen && (
          <WorkspaceDropdownContent
            onCreateNewWorkspace={onCreateNewWorkspace}
            onSelectWorkspace={onSelectWorkspace}
            onViewAll={onViewAllWorkspaces}
            showViewAll={activeTab !== "workspaces"}
          />
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Provider>
  );
};

interface WorkspaceDropdownContentProps {
  onCreateNewWorkspace(): void;
  onSelectWorkspace(workspaceId: string): void;
  onViewAll(): void;
  showViewAll: boolean;
}

const WorkspaceDropdownContent = memo(
  ({ onCreateNewWorkspace, onSelectWorkspace, onViewAll, showViewAll }: WorkspaceDropdownContentProps) => (
    <div className="p-4 flex flex-col gap-y-3 w-[476px] select-none">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-text-base-primary">Recent</h1>
        {showViewAll && (
          <Button variant="ghost" size="sm" onClick={onViewAll}>
            View all
            <Icon size="sm" cursor="pointer" icon="icon-lt-arrow-right-line" />
          </Button>
        )}
      </div>
      <WorkspaceDropdownList onSelectWorkspace={onSelectWorkspace} />
      <div className="border-t-2 border-border-base-divider" />
      <Button variant="ghost" size="md" className="p-1! w-full justify-start" onClick={onCreateNewWorkspace}>
        <Icon
          size="lg"
          cursor="pointer"
          icon="icon-lt-add-line"
          extraClasses="bg-neutral-50 rounded-sm text-text-base-secondary w-10 h-10 flex items-center justify-center"
        />
        New workspace
      </Button>
    </div>
  )
);

WorkspaceDropdownContent.displayName = "WorkspaceDropdownContent";
